import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { HomeComponent } from './modules/home/home.component';
import { NoAccessComponent } from './unauthorized/noaccess.component';
import { UnauthorizedComponent } from './unauthorized/unauthorized.component';
import { CanActivateViaAuthGuard } from './services/authorization.guard';
import { ReportComponent } from './modules/report/report.component';

const routes: Routes = [
  { path: '', component: HomeComponent, pathMatch: 'full' },
  { path: 'home', redirectTo: '' },

  {
    path: 'apps',
    loadChildren: () => import('./modules/apps').then(mod => mod.AppsModule),
    data: { auth: { roleType: 'Administrative' } },
    canActivate: [CanActivateViaAuthGuard]
  },

  { path: 'noaccess', component: NoAccessComponent },
  { path: 'unauthorized', component: UnauthorizedComponent },
  {
    path: 'organization',
    loadChildren: () => import('./modules/organization').then(mod => mod.OrganizationModule),
    data: { auth: { roleType: 'Administrative', claim: 'organizationManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'dataentry',
    loadChildren: () => import('./modules/dataentry').then(mod => mod.DataEntryModule),
    data: { auth: { roleType: 'Administrative', claim: 'dataEntryManagement.User' } },
    // canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'modification.request',
    loadChildren: () => import('./modules/modification.request/modification.request.module').then(mod => mod.ModificationRequestModule),
    data: { auth: { roleType: undefined, claim: 'changeManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'modification.request.history',
    loadChildren: () => import('./modules/modification.request.history/modification.request.history.module').then(mod => mod.ModificationRequestHistoryModule),
    data: { auth: { roleType: undefined, claim: 'changeManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'clarification.request',
    loadChildren: () => import('./modules/clarification.request/clarification.request.module').then(mod => mod.ClarificationRequestModule),
    data: { auth: { roleType: undefined, claim: 'changeManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'clarification.request.history',
    loadChildren: () => import('./modules/clarification.request.history/clarification.request.history.module').then(mod => mod.ClarificationRequestHistoryModule),
    data: { auth: { roleType: undefined, claim: 'changeManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'bulletin',
    loadChildren: () => import('./modules/bulletin/bulletin.module').then(mod => mod.BulletinModule),
    data: { auth: { roleType: undefined, claim: 'bulletinManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'case',
    loadChildren: () => import('./modules/case/case.module').then(mod => mod.CaseModule),
    data: { auth: { roleType: undefined, claim: undefined } },
    //canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'case/unmarked',
    loadChildren: () => import('./modules/case/case.module').then(mod => mod.CaseModule),
    data: { auth: { roleType: 'Administrative', claim: 'caseManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'case.category',
    loadChildren: () => import('./modules/case.category/case.category.module').then(mod => mod.CaseCategoryModule),
    data: { auth: { roleType: 'Administrative', claim: 'caseManagement.SysAdmin' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'clientuser',
    loadChildren: () => import('./modules/user/client.user.module').then(mod => mod.ClientUserModule),
    data: { type: 'Public', auth: { roleType: 'Administrative', claim: 'userManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'profile',
    loadChildren: () => import('./modules/profile/profile.module').then(mod => mod.ProfileModule),
    data: { auth: { claim: 'userManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'officeuser',
    loadChildren: () => import('./modules/user/office.user.module').then(mod => mod.OfficeUserModule),
    data: { type: 'Administrative', auth: { roleType: 'Administrative', claim: 'userManagement.Elevated' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'role',
    loadChildren: () => import('./modules/role').then(mod => mod.RoleModule),
    data: { auth: { roleType: 'Administrative', claim: 'roleManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'label',
    loadChildren: () => import('./modules/label').then(mod => mod.LabelModule),
    data: { auth: { roleType: 'Administrative', claim: 'labelManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'codelist',
    loadChildren: () => import('./modules/codelist').then(mod => mod.CodeListModule),
    data: { auth: { roleType: 'Administrative', claim: 'codeListManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'message.board',
    loadChildren: () => import('./modules/internal.message/internal.message.module').then(mod => mod.InternalMessageModule),
    data: { auth: { roleType: 'Administrative', claim: 'internalMessageManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'maintenance.message',
    loadChildren: () => import('./modules/maintenance.message/maintenance.message.module').then(mod => mod.MaintenanceMessageModule),
    data: { auth: { roleType: 'Administrative', claim: 'maintenanceMessageManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'help.message',
    loadChildren: () => import('./modules/help.message/help.message.module').then(mod => mod.HelpMessageModule),
    data: { auth: { roleType: 'Administrative', claim: 'helpMessageManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'portal.message',
    loadChildren: () => import('./modules/portal.message/portal.message.module').then(mod => mod.PortalMessageModule),
    data: { auth: { roleType: 'Administrative', claim: 'portalMessageManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'help',
    loadChildren: () => import('./modules/help/help.module').then(mod => mod.HelpModule)
  },
  {
    path: 'form',
    loadChildren: () => import('./modules/form').then(mod => mod.FormModule),
    data: { auth: { roleType: 'Administrative', claim: 'formManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'inspection',
    loadChildren: () => import('./modules/inspection/inspection.module').then(mod => mod.InspectionModule),
    data: { auth: { roleType: 'Administrative', claim: 'inspectionManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'inspection.entry',
    loadChildren: () => import('./modules/inspection.entry/inspection.entry.module').then(mod => mod.InspectionEntryModule),
    data: { auth: { roleType: 'Administrative', claim: 'inspectionManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'workflow.instance',
    loadChildren: () => import('./modules/workflow.instance/workflow.instance.module').then(mod => mod.WorkflowInstanceModule),
    data: { auth: { roleType: 'Administrative', claim: 'workflowManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'workflow.template',
    loadChildren: () => import('./modules/workflow.template').then(mod => mod.WorkflowTemplateModule),
    data: { auth: { roleType: 'Administrative', claim: 'workflowManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'task',
    loadChildren: () => import('./modules/task/workflow.instance.thread.task.module').then(mod => mod.WorkflowInstanceThreadTaskModule),
    data: { auth: { roleType: 'Administrative', claim: 'workflowManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'formscript',
    loadChildren: () => import('./modules/form.script').then(mod => mod.FormScriptModule),
    data: { auth: { roleType: 'Administrative', claim: 'formManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'faq.message',
    loadChildren: () => import('./modules/faq.message/faq.message.module').then(mod => mod.FaqMessageModule),
    data: { auth: { roleType: 'Administrative', claim: 'faqMessageManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'dataentry.failed',
    loadChildren: () => import('./modules/dataentry.failed/dataentry.failed.module').then(mod => mod.DataEntryFailedModule),
    data: { auth: { roleType: 'Administrative', claim: 'dataEntryManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'dataentry.info',
    loadChildren: () => import('./modules/dataentry.info/dataEntry.info.module').then(mod => mod.DataEntryInfoModule),
    data: { auth: { roleType: 'Administrative', claim: 'dataEntryManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'batch',
    loadChildren: () => import('./modules/batch/batch.module').then(mod => mod.BatchModule),
    data: { auth: { roleType: 'Administrative', claim: 'batchManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'notification',
    loadChildren: () => import('./modules/notification/notification.module').then(mod => mod.NotificationModule),
    data: { auth: { roleType: undefined, claim: 'notificationManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'customprop.type',
    loadChildren: () => import('./modules/customprop.type/customprop.type.module').then(mod => mod.CustomPropTypeModule),
    data: { auth: { roleType: 'Administrative', claim: 'customPropManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'officialact',
    loadChildren: () => import('./modules/officialact/officialact.module').then(mod => mod.OfficialActModule),
    data: { auth: { roleType: 'Administrative', claim: 'officialActManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  {
    path: 'secgroup',
    loadChildren: () => import('./modules/secgroup/secgroup.module').then(mod => mod.SecGroupModule),
    data: { auth: { roleType: 'Administrative', claim: 'secGroupManagement.User' } },
    canActivate: [CanActivateViaAuthGuard]
  },
  { path: 'reports', component: ReportComponent, pathMatch: 'full' },
  {
    path: '!',
    loadChildren: () => import('./modules/universal/universal.module').then(mod => mod.UniversalModule),
    data: { auth: { roleType: 'Administrative', claim: undefined } },
    canActivate: [CanActivateViaAuthGuard]
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {scrollPositionRestoration: 'enabled'})],
  exports: [RouterModule]
})
export class AppRoutingModule { }



// WEBPACK FOOTER //
// ./src/app/app-routing.module.ts